// Design tokens — Pure white minimal light theme (Linear / Notion style)
// Light surfaces, thin borders, near-black primary, grayscale-focused.
// NOTE: use literal values everywhere (no CSS variables) — var() proved
// unreliable for backgrounds in earlier iterations.

export const colors = {
  // Surfaces
  bg: '#ffffff',
  bgAlt: '#fafafa',
  surface: '#ffffff',
  surfaceAlt: '#f5f5f5', // table header / muted blocks
  surfaceMuted: '#ededed',

  // Translucent overlays (subtle, on white)
  glass: 'rgba(0,0,0,0.02)',
  glassHover: 'rgba(0,0,0,0.04)',

  // Borders
  border: 'rgba(0,0,0,0.08)',
  borderHover: 'rgba(0,0,0,0.16)',

  // Primary / accent (near-black)
  primary: '#18181b',
  primaryLight: 'rgba(24,24,27,0.08)',
  accent: '#18181b',
  accentLight: 'rgba(24,24,27,0.06)',

  // Text
  textPrimary: '#18181b', // zinc-900
  textSecondary: '#6b7280', // gray-500
  textMuted: '#9ca3af', // gray-400
  gray: '#6b7280',

  // Status (deep text-safe + soft pastel backgrounds)
  success: '#16a34a',
  successBg: '#f0fdf4',
  danger: '#dc2626',
  dangerBg: '#fef2f2',
  warning: '#d97706',
  warningBg: '#fffbeb',
  info: '#2563eb',
  infoBg: '#eff6ff',

  // Dashboard chart series (grayscale-leaning for minimal palette)
  requests: '#18181b',
  quota: '#71717a',
  tokens: '#a1a1aa',
};

export const gradients = {
  // Page background — barely-there white → off-white
  pageBackground:
    'radial-gradient(1200px 600px at 50% -10%, #ffffff 0%, #fafafa 60%, #f7f7f8 100%)',
  // Soft grayscale aurora for auth screens (no color)
  aurora:
    'radial-gradient(800px 400px at 20% 10%, rgba(0,0,0,0.025) 0%, transparent 60%),' +
    'radial-gradient(700px 400px at 85% 20%, rgba(0,0,0,0.02) 0%, transparent 55%),' +
    'radial-gradient(900px 500px at 50% 100%, rgba(0,0,0,0.015) 0%, transparent 60%)',
  // Heading text gradient — solid near-black, no color
  text: 'linear-gradient(90deg, #18181b 0%, #000000 100%)',
  // Primary (black) button — subtle depth
  primaryButton: 'linear-gradient(180deg, #18181b 0%, #000000 100%)',
  // Section / card accents
  heroBackground: 'linear-gradient(180deg, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0) 100%)',
  cardHover: 'rgba(0,0,0,0.02)',
  groupRow: 'rgba(0,0,0,0.015)',
  groupRowHover: 'rgba(0,0,0,0.03)',
};

export const shadows = {
  card: '0 1px 2px rgba(0,0,0,0.04), 0 1px 3px rgba(0,0,0,0.06)',
  searchSection: '0 1px 2px rgba(0,0,0,0.04)',
  tableWrap: '0 1px 2px rgba(0,0,0,0.04)',
  // Used on black primary buttons
  glow: '0 1px 2px rgba(0,0,0,0.18)',
  glowHover: '0 2px 10px rgba(0,0,0,0.16)',
  glowSoft: '0 1px 2px rgba(0,0,0,0.04)',
};

export const radius = {
  sm: '6px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  xxl: '20px',
};

export const transitions = {
  fast: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
  normal: '220ms cubic-bezier(0.4, 0, 0.2, 1)',
};

const globalStyles = {
  // Full-page container
  page: {
    minHeight: '100vh',
    background: gradients.pageBackground,
    color: colors.textPrimary,
    padding: '32px 24px 64px',
  },

  // Card surface
  card: {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: radius.xl,
    boxShadow: shadows.card,
    padding: 24,
  },

  // Hero / title block above content
  hero: {
    background: gradients.heroBackground,
    borderBottom: `1px solid ${colors.border}`,
    borderRadius: radius.lg,
    padding: '20px 24px',
    marginBottom: 20,
  },

  // Toolbar / search section
  searchSection: {
    background: colors.glass,
    border: `1px solid ${colors.border}`,
    borderRadius: radius.lg,
    padding: 16,
    boxShadow: shadows.searchSection,
  },

  // Table wrapper — NO overflow:hidden (avoids header clipping)
  tableWrap: {
    background: colors.bg,
    border: `1px solid ${colors.border}`,
    borderRadius: radius.xl,
    boxShadow: shadows.tableWrap,
    overflow: 'visible',
  },

  // Table header row
  tableHeader: {
    background: `${colors.surfaceAlt} !important`,
    color: `${colors.textSecondary} !important`,
    fontWeight: 600,
    fontSize: '0.85em',
    letterSpacing: '0.02em',
    borderBottom: `1px solid ${colors.border} !important`,
  },

  // Table body row
  tableRow: {
    color: colors.textPrimary,
    transition: transitions.normal,
    borderBottom: `1px solid ${colors.border}`,
  },

  // Table row hover — soft gray, no inset accent edge
  tableRowHover: {
    background: 'rgba(0,0,0,0.02)',
    color: colors.textPrimary,
  },

  // Ghost / secondary button
  button: {
    background: colors.surface,
    color: colors.textPrimary,
    border: `1px solid ${colors.border}`,
    borderRadius: radius.md,
    transition: transitions.fast,
  },

  // Primary (black) button
  primaryButton: {
    background: gradients.primaryButton,
    color: '#ffffff',
    border: '1px solid #000000',
    borderRadius: radius.md,
    boxShadow: shadows.glow,
    transition: transitions.fast,
  },

  // Group / summary row
  groupRow: {
    background: gradients.groupRow,
    color: colors.textSecondary,
    fontWeight: 600,
    transition: transitions.normal,
  },

  groupRowHover: {
    background: gradients.groupRowHover,
    color: colors.textPrimary,
  },

  // Footer
  footer: {
    color: colors.textMuted,
    borderTop: `1px solid ${colors.border}`,
    background: colors.bgAlt,
  },
};

export default globalStyles;
